import { Button, Card, Divider, Modal, Segmented, Slider, notification } from "antd";
import { observer } from "mobx-react";
import { forwardRef, useImperativeHandle, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { formatNumberClearly } from "../../common/utils";

const Plan = observer(
  forwardRef((_, ref) => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("monthly");
    const [linkCount, setLinkCount] = useState(2000);
    const { t } = useTranslation();

    const onCancel = () => {
      setOpen(false);
      setTab("monthly");
    };

    useImperativeHandle(ref, () => ({
      show: () => setOpen(true),
    }));

    const linkPrice = 6.9 * (linkCount / 1000);

    const handleSendInquiry = (planName) => {
      onCancel();
      notification.open({
        message: null,
        description: (
          <div className="text-center !py-3 !px-4">
            <div className="text-5xl mb-2">🎉</div>
            <div className="font-bold text-xl mb-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-transparent bg-clip-text">
              {t("Thank you for your interest!")}
            </div>
            <div className="mb-2">
              <Trans
                i18nKey="You have selected the <span>{{planName}}</span> plan."
                values={{ planName: t(planName) }}
                components={{ span: <span className="text-main" /> }}
              />
            </div>
            <div className="text-gray-600 mb-2">
              {t("Our sales team will follow up via email shortly to help with the upgrade.")}
            </div>
            <div className="text-xs text-gray-400">
              <Trans
                i18nKey="We appreciate your trust in <span>Airflex</span>"
                components={{ span: <span className="text-main" /> }}
              />{" "}
              🚀
            </div>
          </div>
        ),
        placement: "top",
        duration: 0,
        style: { minWidth: 400, maxWidth: 480, textAlign: "center" },
      });
    };

    return (
      <>
        <Modal title={null} open={open} onCancel={onCancel} width={840} footer={null} maskClosable={false} centered>
          <div className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-500 mb-1">
            {t("Airflex Pricing")}
          </div>
          <div className="flex flex-col justify-center items-center p-2 md:p-4 w-full">
            <div className="text-center">{t("Choose a plan that fits with your project and your business size.")}</div>
            <div className="text-main font-medium !mb-4">{t("No credit card required.")}</div>
            {/* Segmented Tabs */}
            <Segmented
              block
              options={[
                { label: t("Monthly Package"), value: "monthly" },
                { label: t("Link Package"), value: "link" },
              ]}
              value={tab}
              onChange={setTab}
              className="min-w-[340px]"
            />

            {/* Nội dung từng tab */}
            {tab === "monthly" ? (
              <div className="flex flex-row gap-4 w-full justify-center overflow-x-auto flex-nowrap !pt-10 !pb-3 px-2 h-[440px]">
                {/* Basic */}
                <Card
                  className="max-w-xs w-[260px] min-w-[240px] text-center rounded-2xl !shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 bg-gradient-to-br from-gray-100 to-white"
                  bordered={false}
                  bodyStyle={{ padding: "16px 24px" }}
                >
                  <div className="flex flex-col items-center mb-1">
                    <span className="text-2xl">🔰</span>
                    <div className="font-bold text-base">{t("BASIC")}</div>
                  </div>
                  <div className="mb-2">
                    <div className="text-3xl font-bold text-gray-800">$0</div>
                    <div className="text-xs text-green-500 font-semibold tracking-wide">{t("CURRENT PLAN")}</div>
                  </div>
                  <div className="text-xs text-tgray">
                    {t("For small app owners looking for reliable deep link solution")}
                  </div>
                  <Divider className="my-2" />
                  <ul className="text-xs text-gray-600 mb-4 space-y-1">
                    <li>1000 {t("links")}</li>
                    <li>{t("Up to 100,000 traffic requests")}</li>
                    <li>{t("Unlimited apps integration")}</li>
                    <li>{t("Analytics & attributions")}</li>
                    <li>{t("SDK integration")}</li>
                  </ul>
                </Card>
                {/* Pro */}
                <Card
                  className="max-w-xs w-[260px] min-w-[240px] text-center rounded-2xl !shadow-md hover:shadow-2xl hover:scale-105 transition-all duration-300 bg-gradient-to-br from-blue-100 to-blue-50 relative"
                  bordered={false}
                  bodyStyle={{ padding: "16px 24px" }}
                >
                  <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg z-10 flex items-center justify-center gap-2 whitespace-nowrap">
                    <span>⭐</span> {t("RECOMMENDED")}
                  </div>
                  <div className="flex flex-col items-center mb-1">
                    <span className="text-2xl">🚀</span>
                    <div className="font-bold text-base text-blue-600">{t("PRO")}</div>
                  </div>
                  <div className="text-3xl font-bold text-blue-600 h-[52px] mb-2 flex-center">$39</div>
                  <div className="text-xs text-tgray">{t("For growing apps planning to scale up")}</div>
                  <Divider className="my-2" />
                  <ul className="text-xs text-gray-700 space-y-1">
                    <li>10,000 {t("links")}</li>
                    <li>{t("Up to 1,000,000 traffic requests")}</li>
                    <li>{t("Unlimited apps integration")}</li>
                    <li>{t("Analytics & attributions")}</li>
                    <li>{t("SDK integration")}</li>
                    <li className="font-bold">{t("Custom domain")}</li>
                  </ul>
                  <div className="absolute bottom-5 left-1/2 -translate-x-1/2">
                    <Button
                      type="primary"
                      className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full px-4 py-2 text-xs md:text-sm font-bold hover:scale-105 duration-200 flex-center"
                      onClick={() => handleSendInquiry("PRO")}
                    >
                      {t("SEND INQUIRY")}
                    </Button>
                  </div>
                </Card>
                {/* Enterprise */}
                <Card
                  className="max-w-xs w-[260px] min-w-[240px] text-center rounded-2xl !shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 bg-gradient-to-br from-purple-100 to-white"
                  bordered={false}
                  bodyStyle={{ padding: "16px 24px" }}
                >
                  <div className="flex flex-col items-center mb-1">
                    <span className="text-2xl">👑</span>
                    <div className="font-bold text-base text-purple-600">{t("ENTERPRISE")}</div>
                  </div>
                  <div className="text-lg md:text-xl md:leading-9 font-bold text-purple-700 h-[52px] mb-2 flex-center">
                    {t("Custom Pricing")}
                  </div>
                  <div className="text-xs text-tgray">{t("For apps with custom needs and large-scale growth")}</div>
                  <Divider className="my-2" />
                  <ul className="text-xs text-gray-700 space-y-1">
                    <li>{t("All benefits of Pro plan, plus:")}</li>
                    <li className="font-bold">{t("Custom monthly link quota")}</li>
                    <li className="font-bold">{t("Deep link API solution")}</li>
                    <li className="font-bold">{t("Engagement studio")}</li>
                    <li className="font-bold">{t("Open data API")}</li>
                    <li className="font-bold">{t("Priority support")}</li>
                  </ul>
                  <div className="absolute bottom-5 left-1/2 -translate-x-1/2">
                    <Button
                      type="primary"
                      className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full px-4 py-2 text-xs md:text-sm font-bold hover:scale-105 duration-200 flex-center"
                      onClick={() => handleSendInquiry("ENTERPRISE")}
                    >
                      {t("SEND INQUIRY")}
                    </Button>
                  </div>
                </Card>
              </div>
            ) : (
              <div className="w-full flex flex-col items-center h-[440px]">
                <div className="w-full md:w-3/4 !my-5">
                  <Slider
                    min={1000}
                    max={10000}
                    step={1000}
                    value={linkCount}
                    onChange={setLinkCount}
                    tooltip={{ open: false }}
                  />
                  <div className="text-blue-600 font-bold text-center">
                    {linkCount} {t("links")}
                  </div>
                </div>
                <div className="text-center">
                  <div className="flex-center">
                    <Card
                      className="text-center rounded-2xl !shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 bg-gradient-to-br from-purple-100 to-blue-100 !px-4"
                      bordered={false}
                    >
                      <div className="font-bold text-base mb-1">{t("LINK PACKAGE")}</div>
                      <div className="text-2xl font-bold mb-1 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-500">
                        ${linkPrice.toFixed(1)}
                      </div>
                      <div className="text-xs text-tgray">{t("PER MONTH")}</div>
                    </Card>
                  </div>
                  <div className="!mt-4 text-tgray">{t("For apps targeting flexible quota or short-term testing")}</div>
                  <Divider className="!my-3 border-indigo-200" />
                  <ul>
                    <li>
                      {t("Price per {{linkCount}} deep links", { linkCount: formatNumberClearly(linkCount, 3, ",") })}
                    </li>
                    <li>
                      {t("Up to {{trafficRequests}} traffic requests", {
                        trafficRequests: formatNumberClearly(linkCount * 100, 3, ","),
                      })}
                    </li>
                    <li>{t("Add directly to your monthly quota")}</li>
                  </ul>
                  <div className="flex-center !mt-4">
                    <Button
                      type="primary"
                      className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-full px-4 py-2 text-xs md:text-sm font-bold hover:scale-105 duration-200 flex-center"
                      onClick={() => handleSendInquiry("LINK PACKAGE")}
                    >
                      {t("SEND INQUIRY")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      </>
    );
  })
);

export default Plan;
